// powerSlice.ts - Manages UI-specific state for Power/Generator components
// This slice contains derived/computed state from raw API data stored in dashboardSlice

import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { GraphData, GraphDataResponse, LastKnownBeforeRange, LiveDataItem, LiveDataItems } from '../types/powerTypes';
import { Equipment, detailedGeneratorData } from '../types/powerTypes';
import type { MonthlyKwhResponse, MonthlyEngineHoursResponse } from '../services/powerService';
import { fetchAndFilterGraphData, fetchMonthlyKwh, fetchMonthlyEngineHours } from '../services/powerService';
import { fetchBPLiveDataForCustomerForOneSite } from '../services/dashboardService';
import dayjs from 'dayjs';

/**
 * PowerUISlice - Manages UI-specific state for Power Generation components
 * - Derived equipment lists (generatorList, fuelGenList) from dashboardSlice data
 * - Selected equipment and live data values for UI display
 * - Graph/chart data for time-series visualizations
 * - Fuel percentage and other computed values
 */
interface PowerUIState {
    // Derived equipment lists from dashboardSlice.detailedGeneratorDataForSite


    // Graph/chart data
    graphData: GraphData[];
    lastKnownBeforeRange: LastKnownBeforeRange | null;
    graphDataLoading: boolean;

    // Selected equipment and live values for UI
    selectedGeneratorId: number | null;
    selectedFuelId: number | null;
    selectedSourceId: string;
    selectedFuelSourceId: string;
    liveDataArray: LiveDataItem[];
    liveDataArrayFull: LiveDataItems | null;
    bpValue: string;
    bpValueArray: string[];

    // Fuel-specific computed state
    fuelPercentage: number | undefined | null;
    fuelBpName: string;

    // Monthly data
    monthlyKwh: MonthlyKwhResponse | null;
    monthlyKwhLoading: boolean;
    monthlyEngineHours: MonthlyEngineHoursResponse | null;
    monthlyEngineHoursLoading: boolean;

    // Time range selection
    selectedTimePeriod: string;
    fromDate: string | null;
    toDate: string | null;

    // Loading and error states
    loading: boolean;
    error: string | null;
    errorMessage: string | null;
    canceled: boolean;
}

const initialState: PowerUIState = {
    // Derived equipment lists

    // Graph/chart data
    graphData: [],
    lastKnownBeforeRange: null,
    graphDataLoading: false,

    // Selected equipment and live values
    selectedGeneratorId: null,
    selectedFuelId: null,
    selectedSourceId: '',
    selectedFuelSourceId: '',
    liveDataArray: [],
    liveDataArrayFull: null,
    bpValue: '',
    bpValueArray: [],

    // Fuel-specific computed state
    fuelPercentage: null,
    fuelBpName: '',

    // Monthly data
    monthlyKwh: null,
    monthlyKwhLoading: false,
    monthlyEngineHours: null,
    monthlyEngineHoursLoading: false,

    // Time range selection
    selectedTimePeriod: 'last7days',
    fromDate: dayjs().startOf('day').format('YYYY-MM-DD'),
    toDate: dayjs().endOf('day').format('YYYY-MM-DD'),

    // Loading and error states
    loading: false,
    error: null,
    errorMessage: null,
    canceled: false,
};

const generatorListSlice = createSlice({
    name: 'generatorList',
    initialState,
    reducers: {
        clearGeneratorList(state) {
            state.error = null;
            state.canceled = false;
        },
        clearPowerState(state) {
            return {
                ...initialState,
                bpValue: state.bpValue,
                selectedTimePeriod: state.selectedTimePeriod,
            }
        },
        clearGraphData(state) {
            state.graphData = [];
            state.lastKnownBeforeRange = null;
            state.error = null;
            state.canceled = false;
        },
        clearGenChangeData(state) {
            state.bpValueArray = [];
            state.bpValue = state.bpValue;
            state.selectedGeneratorId = null;
            state.selectedSourceId = '';
        },
        setSelectedTimePeriod(state, action: PayloadAction<string>) {
            state.selectedTimePeriod = action.payload;
        },
        setSelectedLiveDataArrayFull(state, action: PayloadAction<LiveDataItems | null>) {
            state.liveDataArrayFull = action.payload;
        },
        setSelectedLiveDataArray(state, action: PayloadAction<LiveDataItem[] | null>) {
            if (action.payload) {
                state.liveDataArray = action.payload;
                const fuelLevelRegex = /Fuel Level(\s*\(.*\))?/i;
                state.fuelPercentage = action.payload.find(item => fuelLevelRegex.test(item.value_name))?.value ?? null;
                state.bpValueArray = action.payload.map(item => item.value_name);
                state.fuelBpName = action.payload.map(item => item.value_name).filter(item => fuelLevelRegex.test(item))[0] ?? '';
            } else {
                state.liveDataArray = [];
                state.fuelPercentage = null;
                state.bpValueArray = [];
                state.fuelBpName = '';
            }
        },
        setFuelPercentage(state, action: PayloadAction<number | null | undefined>) {
            state.fuelPercentage = action.payload;
        },
        setFromDate(state, action: PayloadAction<string>) {
            state.fromDate = action.payload;
        },
        setToDate(state, action: PayloadAction<string>) {
            state.toDate = action.payload;
        },
        setSelectedGeneratorId(state, action: PayloadAction<number | null>) {
            state.selectedGeneratorId = action.payload;
        },
        setSelectedFuelId(state, action: PayloadAction<number | null>) {
            state.selectedFuelId = action.payload;
        },
        setSelectedFuelSourceId(state, action: PayloadAction<string>) {
            state.selectedFuelSourceId = action.payload
        },
        setSelectedSourceId(state, action: PayloadAction<string>) {
            state.selectedSourceId = action.payload
        },
        setBpValue(state, action: PayloadAction<string>) {
            state.bpValue = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAndFilterGraphData.pending, (state) => {
                // Only show loading on initial fetch (when data is empty)
                state.graphDataLoading = true;
                state.error = null;
            })
            .addCase(fetchAndFilterGraphData.fulfilled, (state, action: PayloadAction<GraphDataResponse>) => {
                state.graphData = action.payload.data;
                state.lastKnownBeforeRange = action.payload.lastKnownBeforeRange;
                state.graphDataLoading = false;
            })
            .addCase(fetchAndFilterGraphData.rejected, (state, action) => {
                state.error = action.payload ?? 'Error fetching data';
                if (action.payload && (action.payload === 'Request canceled')) {
                    // Keep loading state if no data exists
                    state.graphDataLoading = state.graphData.length === 0;
                }
                else state.graphDataLoading = false;
            })
            .addCase(fetchMonthlyKwh.pending, (state) => {
                state.monthlyKwhLoading = true;
                state.error = null;
            })
            .addCase(fetchMonthlyKwh.fulfilled, (state, action: PayloadAction<MonthlyKwhResponse>) => {
                state.monthlyKwh = action.payload;
                state.monthlyKwhLoading = false;
            })
            .addCase(fetchMonthlyKwh.rejected, (state, action) => {
                state.error = action.payload ?? 'Error fetching monthly kWh';
                state.monthlyKwhLoading = false;
                state.monthlyKwh = null;
            })
            .addCase(fetchMonthlyEngineHours.pending, (state) => {
                state.monthlyEngineHoursLoading = true;
                state.error = null;
            })
            .addCase(fetchMonthlyEngineHours.fulfilled, (state, action: PayloadAction<MonthlyEngineHoursResponse>) => {
                state.monthlyEngineHours = action.payload;
                state.monthlyEngineHoursLoading = false;
            })
            .addCase(fetchMonthlyEngineHours.rejected, (state, action) => {
                state.error = action.payload ?? 'Error fetching monthly engine hours';
                state.monthlyEngineHoursLoading = false;
                state.monthlyEngineHours = null;
            })
    },
});

export const {
    setSelectedLiveDataArray,
    setSelectedLiveDataArrayFull,
    clearPowerState,
    setFromDate,
    setToDate,
    clearGenChangeData,
    setFuelPercentage,
    clearGeneratorList,
    setSelectedTimePeriod,
    setBpValue,
    setSelectedSourceId,
    setSelectedGeneratorId,
    clearGraphData,
    setSelectedFuelId,
    setSelectedFuelSourceId
} = generatorListSlice.actions;

export default generatorListSlice.reducer;